// Utilidades para manejar días hábiles y festivos en Colombia

/**
 * Festivos fijos de Colombia (que no cambian de fecha cada año)
 */
const FIXED_HOLIDAYS = [
  { month: 1, day: 1 },   // Año Nuevo
  { month: 5, day: 1 },   // Día del Trabajo
  { month: 7, day: 20 },  // Día de la Independencia
  { month: 8, day: 7 },   // Batalla de Boyacá
  { month: 12, day: 8 },  // Inmaculada Concepción
  { month: 12, day: 25 }  // Navidad
];

/**
 * Festivos que se trasladan al lunes siguiente si caen en día diferente
 * (Ley Emiliani)
 */
const EMILIANI_HOLIDAYS = [
  { month: 1, day: 6 },   // Epifanía
  { month: 3, day: 19 },  // San José
  { month: 6, day: 29 },  // San Pedro y San Pablo
  { month: 8, day: 15 },  // Asunción de la Virgen
  { month: 10, day: 12 }, // Día de la Raza
  { month: 11, day: 1 },  // Todos los Santos
  { month: 11, day: 11 }  // Independencia de Cartagena
];

/**
 * Calcula la fecha de Pascua para un año dado usando el algoritmo de Gauss
 * @param {number} year - Año
 * @returns {Date} - Fecha de Pascua
 */
function calculateEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
}

/**
 * Obtiene la fecha del lunes siguiente a una fecha dada
 * @param {Date} date - Fecha base
 * @returns {Date} - Fecha del lunes siguiente
 */
function getNextMonday(date) {
  const result = new Date(date);
  const dayOfWeek = result.getDay();
  const daysToAdd = dayOfWeek === 0 ? 1 : 8 - dayOfWeek; // Si es domingo (0), agregar 1; sino, 8 - día
  result.setDate(result.getDate() + daysToAdd);
  return result;
}

/**
 * Obtiene todos los festivos de Colombia para un año dado
 * @param {number} year - Año
 * @returns {Date[]} - Array de fechas de festivos
 */
export function getColombianHolidays(year) {
  const holidays = [];
  
  // Festivos fijos
  FIXED_HOLIDAYS.forEach(holiday => {
    holidays.push(new Date(year, holiday.month - 1, holiday.day));
  });
  
  // Festivos con Ley Emiliani (se trasladan al lunes)
  EMILIANI_HOLIDAYS.forEach(holiday => {
    const date = new Date(year, holiday.month - 1, holiday.day);
    const dayOfWeek = date.getDay();
    
    if (dayOfWeek === 1) {
      // Si cae lunes, se mantiene
      holidays.push(date);
    } else {
      // Si no cae lunes, se traslada al siguiente lunes
      holidays.push(getNextMonday(date));
    }
  });
  
  // Festivos relacionados con Pascua
  const easter = calculateEaster(year);
  
  // Jueves Santo (3 días antes de Pascua)
  const holyThursday = new Date(easter);
  holyThursday.setDate(easter.getDate() - 3);
  holidays.push(holyThursday);
  
  // Viernes Santo (2 días antes de Pascua)
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  holidays.push(goodFriday);
  
  // Ascensión del Señor (39 días después de Pascua, trasladado al lunes)
  const ascension = new Date(easter);
  ascension.setDate(easter.getDate() + 39);
  holidays.push(getNextMonday(ascension));
  
  // Corpus Christi (60 días después de Pascua, trasladado al lunes)
  const corpusChristi = new Date(easter);
  corpusChristi.setDate(easter.getDate() + 60);
  holidays.push(getNextMonday(corpusChristi));
  
  // Sagrado Corazón de Jesús (68 días después de Pascua, trasladado al lunes)
  const sacredHeart = new Date(easter);
  sacredHeart.setDate(easter.getDate() + 68);
  holidays.push(getNextMonday(sacredHeart));
  
  return holidays.sort((a, b) => a - b);
}

/**
 * Verifica si una fecha es festivo en Colombia
 * @param {Date} date - Fecha a verificar
 * @returns {boolean} - True si es festivo
 */
export function isColombianHoliday(date) {
  const holidays = getColombianHolidays(date.getFullYear());
  const dateString = date.toDateString();
  
  return holidays.some(holiday => holiday.toDateString() === dateString);
}

/**
 * Verifica si una fecha es día hábil (lunes a viernes, no festivo)
 * @param {Date} date - Fecha a verificar
 * @returns {boolean} - True si es día hábil
 */
export function isBusinessDay(date) {
  const dayOfWeek = date.getDay();
  
  // Verificar que sea lunes a viernes (1-5)
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false;
  }
  
  // Verificar que no sea festivo
  return !isColombianHoliday(date);
}

/**
 * Agrega días hábiles a una fecha
 * @param {Date} startDate - Fecha inicial
 * @param {number} businessDays - Número de días hábiles a agregar
 * @returns {Date} - Fecha resultante
 */
export function addBusinessDays(startDate, businessDays) {
  const result = new Date(startDate);
  let daysAdded = 0;
  
  while (daysAdded < businessDays) {
    result.setDate(result.getDate() + 1);
    
    if (isBusinessDay(result)) {
      daysAdded++;
    }
  }
  
  return result;
}

/**
 * Calcula el número de días hábiles entre dos fechas
 * @param {Date} startDate - Fecha inicial
 * @param {Date} endDate - Fecha final
 * @returns {number} - Número de días hábiles
 */
export function getBusinessDaysBetween(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let businessDays = 0;
  
  // Asegurar que start sea menor que end
  if (start > end) {
    return 0;
  }
  
  const current = new Date(start);
  
  while (current <= end) {
    if (isBusinessDay(current)) {
      businessDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return businessDays;
}

/**
 * Verifica si han pasado los días hábiles especificados desde una fecha
 * @param {Date} startDate - Fecha inicial
 * @param {number} businessDays - Número de días hábiles
 * @returns {boolean} - True si han pasado los días hábiles
 */
export function hasBusinessDaysPassed(startDate, businessDays) {
  const deadline = addBusinessDays(startDate, businessDays);
  const now = new Date();
  
  return now >= deadline;
}